import Link from 'next/link';
import { SkuT } from '@lambda/apis/src/catalog-v2/types';
import classNames from 'classnames';
import i18n from '@/settings/i18n';
import { priceFormater } from '@/settings';
import { generateSavingsPercentage } from '../../product/hooks/helpers';
import { generateOfferImage } from '../../product/sections/main/left/image/helpers';
import LazyImage from '../LazyImage';
import { priceFormatSuperscript } from '@/lib/price';

const t = i18n.init('shared');

export default function DealCard({
  sku,
  className,
  isHighlighted = false,
}: {
  sku: SkuT;
  className?: string;
  isHighlighted?: boolean;
}) {
  const offer = sku?.offers?.[0];
  const skuProduct = sku?.product;

  if (!offer || !sku || !skuProduct) {
    return null;
  }

  const compareAtPrice = sku.compareAtPrice ?? null;

  const percentageSaving = generateSavingsPercentage(
    offer.price,
    compareAtPrice,
  );

  const link = `${encodeURIComponent(
    skuProduct.slug,
  )}?skuId=${encodeURIComponent(sku.id)}`;

  const image = generateOfferImage(sku, skuProduct);
  const { title } = sku;

  const price = offer.price / 100;

  return (
    <Link
      href={link}
      className={classNames(
        'flex h-full min-h-[265px] cursor-pointer rounded-md border border-gray-500 !bg-white p-4 text-left text-xs',
        { 'flex-row gap-6': isHighlighted, 'flex-col': !isHighlighted },
        className,
      )}
    >
      <div className="relative mx-auto mb-6 mt-4 min-h-[80px] w-full max-w-[130px] lg:min-h-[80px] xl:min-h-[90px]">
        <LazyImage
          src={image}
          alt={title}
          fill
          className="object-contain"
          loading={isHighlighted ? 'eager' : 'lazy'}
        />
      </div>
      <div
        className={classNames('flex flex-1 flex-col', {
          'justify-between': !isHighlighted,
          'justify-center': isHighlighted,
        })}
      >
        <h3
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className="two-line-ellipsis text-[13px] font-medium leading-tight"
        >
          {title}
        </h3>
        <div className="mt-6 flex flex-wrap-reverse items-end justify-between gap-x-3 gap-y-1">
          <div>
            {compareAtPrice && percentageSaving ? (
              <span className="mb-0.5 block text-xs text-gray-700/50">
                {t`new`}{' '}
                <span className="line-through" translate="no">
                  {priceFormatSuperscript(
                    priceFormater.long(compareAtPrice / 100),
                  )}
                </span>
              </span>
            ) : null}
            <div className="flex items-center gap-2">
              <span className="text-base font-bold" translate="no">
                {priceFormatSuperscript(priceFormater.long(price))}
              </span>
              {percentageSaving && percentageSaving > 3 ? (
                <div className="flex h-6 items-center justify-center bg-[#FEF0F1] px-1.5 text-xs font-bold text-[#E20A45]">
                  -{percentageSaving}%
                </div>
              ) : null}
            </div>
          </div>
        </div>
        {isHighlighted && (
          <button className="mt-4 flex h-10 w-full items-center justify-center rounded-md bg-gray-700 font-bold text-white">
            {t`Shop Now`}
          </button>
        )}
      </div>
    </Link>
  );
}
